import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router'
import { AlertTriangle, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../contexts/useAuth'
import { Checkbox } from '../../components/ui/Checkbox'
import { isTwoFactorRequired } from '../../types/auth'
import type { AxiosError } from 'axios'

export function LoginPage() {
  const { login, completeTwoFactorLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [spiralViewBox, setSpiralViewBox] = useState('0 0 0 0')

  // Step 2FA: password già verificata, in attesa del codice SMS
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [twoFactorCode, setTwoFactorCode] = useState('')

  const sectionRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const gvRef = useRef<HTMLDivElement>(null)
  const ghRef = useRef<HTMLDivElement>(null)
  const spiralPathRef = useRef<SVGPathElement>(null)

  // Linee guida e spirale aurea non dettano la posizione della card: la seguono.
  // L'angolo in alto a destra del pannello (centrato) viene misurato dal vero via
  // ResizeObserver, così la geometria resta agganciata anche se il contenuto cresce
  // (es. quando compare l'alert d'errore).
  useEffect(() => {
    const section = sectionRef.current
    const panel = panelRef.current
    const gv = gvRef.current
    const gh = ghRef.current
    const path = spiralPathRef.current
    if (!section || !panel || !gv || !gh || !path) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let firstRun = true

    function buildSpiralPath(cx: number, cy: number) {
      const phi = 1.6180339887
      const b = Math.log(phi) / (Math.PI / 2) // tasso reale della spirale aurea: raggio ×phi ogni quarto di giro
      const a = 3
      let maxR = Math.min(cx, cy) - 30
      if (maxR < 60) maxR = 60
      const thetaMax = Math.log(maxR / a) / b // gira finché non riempie lo spazio disponibile
      // l'angolo di arrivo è fisso (sempre dritto a sinistra), non quello di partenza:
      // altrimenti la coda esterna termina dove capita - a seconda di quanti giri servono
      // per riempire lo spazio, può spuntare fuori in un punto scoperto invece che rientrare
      const endAngle = Math.PI
      const startAngle = endAngle - thetaMax
      const steps = Math.max(160, Math.ceil(thetaMax * 24))
      const pts: string[] = []
      for (let i = 0; i <= steps; i++) {
        const theta = (i / steps) * thetaMax
        const r = a * Math.exp(b * theta)
        const ang = startAngle + theta
        const x = cx + r * Math.cos(ang)
        const y = cy + r * Math.sin(ang)
        pts.push(`${x.toFixed(2)},${y.toFixed(2)}`)
      }
      return `M${pts.join(' L')}`
    }

    function sync() {
      if (!section || !panel || !gv || !gh || !path) return
      const sRect = section.getBoundingClientRect()
      const pRect = panel.getBoundingClientRect()
      const cx = pRect.right - sRect.left
      const cy = pRect.top - sRect.top

      gv.style.left = `${cx}px`
      gh.style.top = `${cy}px`
      setSpiralViewBox(`0 0 ${sRect.width} ${sRect.height}`)
      path.setAttribute('d', buildSpiralPath(cx, cy))

      if (!reduced) {
        const len = path.getTotalLength()
        path.style.strokeDasharray = String(len)
        if (firstRun) {
          path.style.strokeDashoffset = String(len)
          path.animate(
            [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
            { duration: 1500, delay: 150, easing: 'cubic-bezier(.2,.6,.2,1)', fill: 'forwards' }
          )
        } else {
          path.style.strokeDashoffset = '0' // già rivelata: gli aggiornamenti successivi non ripartono da capo
        }
      } else {
        path.style.strokeDasharray = 'none'
        path.style.strokeDashoffset = '0'
      }
      firstRun = false
    }

    sync()
    window.addEventListener('resize', sync)
    let observer: ResizeObserver | undefined
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(sync)
      observer.observe(panel)
    }
    return () => {
      window.removeEventListener('resize', sync)
      observer?.disconnect()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await login({ email, password, rememberMe })
      if (isTwoFactorRequired(result)) {
        setMfaToken(result.mfaToken)
        return
      }
      navigate(from, { replace: true })
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      setError(axiosError.response?.data?.message || 'Credenziali non valide')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaToken) return
    setError('')
    setLoading(true)
    try {
      await completeTwoFactorLogin(mfaToken, twoFactorCode)
      navigate(from, { replace: true })
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      setError(axiosError.response?.data?.message || 'Codice non valido')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      ref={sectionRef}
      className="relative flex min-h-dvh w-full items-center justify-center overflow-hidden bg-surface p-4 text-heading"
    >
      {/* sotto i 640px la card occupa quasi tutta la larghezza: il suo angolo lascerebbe
          alla spirale troppo poco spazio per aprirsi in modo leggibile */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full max-[640px]:hidden" viewBox={spiralViewBox}>
        <path ref={spiralPathRef} fill="none" stroke="var(--color-primary-400)" strokeWidth={1.4} opacity={0.9} />
      </svg>
      <div
        ref={gvRef}
        className="animate-guide-draw-v pointer-events-none absolute top-0 bottom-0 w-px origin-top-left scale-y-0 bg-heading/16"
      />
      <div
        ref={ghRef}
        className="animate-guide-draw-h pointer-events-none absolute left-0 right-0 h-px origin-top-left scale-x-0 bg-heading/16"
      />

      <span className="pointer-events-none absolute top-6 right-8 font-mono text-xs tracking-wider text-link">Φ 1.618</span>
      <span className="pointer-events-none absolute right-8 bottom-6 font-mono text-xs tracking-wider text-link">© 2026 DomusAurea</span>

      <div className="pointer-events-none absolute top-6 left-8 flex items-center gap-2.5">
        <img src="/apple-touch-icon.png" alt="" className="h-5 w-5 rounded-md" />
        <span className="font-mono text-xs tracking-wider text-link uppercase">DomusAurea</span>
      </div>

      <div
        ref={panelRef}
        className="animate-panel-in relative z-10 my-8 max-h-[calc(100dvh-64px)] w-full max-w-100 overflow-y-auto border border-border-subtle bg-canvas p-11 opacity-0 shadow-2xl"
      >
        {mfaToken ? (
          <>
            <h1 className="mb-2 text-2xl font-semibold [font-variant-caps:small-caps]">Verifica in due passaggi</h1>
            <p className="mb-7 text-sm text-muted">Inserisci il codice a 6 cifre inviato al tuo numero di telefono</p>

            {error && (
              <div className="mb-5 flex items-start gap-2.5 border border-danger-200 border-l-4 border-l-danger-500 bg-danger-50 px-3.5 py-3 text-sm leading-relaxed text-negative">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleVerifyTwoFactor} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="twoFactorCode" className="text-xs tracking-wide text-muted uppercase">Codice di verifica</label>
                <input
                  id="twoFactorCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="h-12 w-full rounded-lg border border-border-subtle bg-surface px-4 text-center text-lg tracking-widest text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none"
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="flex h-13 items-center justify-center gap-2 rounded-lg bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-75 disabled:shadow-none disabled:hover:translate-y-0"
                disabled={loading || twoFactorCode.length !== 6}
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-secondary-50/35 border-t-secondary-50" role="status" aria-hidden="true" />
                    Verifica in corso...
                  </>
                ) : (
                  <>
                    <ShieldCheck size={18} /> Verifica
                  </>
                )}
              </button>

              <button
                type="button"
                className="text-center text-xs text-muted hover:underline"
                onClick={() => {
                  setMfaToken(null)
                  setTwoFactorCode('')
                  setError('')
                }}
              >
                Torna al login
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-2xl font-semibold [font-variant-caps:small-caps]">Bentornato</h1>
            <p className="mb-7 text-sm text-muted">Accedi per continuare a lavorare</p>

            {error && (
              <div className="mb-5 flex items-start gap-2.5 border border-danger-200 border-l-4 border-l-danger-500 bg-danger-50 px-3.5 py-3 text-sm leading-relaxed text-negative">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-xs tracking-wide text-muted uppercase">Email</label>
                <input
                  id="email"
                  type="email"
                  className="h-12 w-full rounded-lg border border-border-subtle bg-surface px-4 text-base text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none sm:text-sm"
                  placeholder="nome@agenzia.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-xs tracking-wide text-muted uppercase">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="h-12 w-full rounded-lg border border-border-subtle bg-surface pr-11 pl-4 text-base text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none sm:text-sm"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute top-0 right-0 flex h-12 w-11 items-center justify-center text-muted hover:text-heading focus-visible:rounded-md focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="-my-1 flex items-center justify-between text-xs text-muted">
                <Checkbox
                  id="rememberMe"
                  label="Ricordami"
                  labelClassName="flex cursor-pointer items-center gap-1.5 select-none"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <Link to="/forgot-password" className="text-link hover:underline">Password dimenticata?</Link>
              </div>

              <button
                type="submit"
                className="flex h-13 items-center justify-center gap-2 rounded-lg bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-75 disabled:shadow-none disabled:hover:translate-y-0"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-secondary-50/35 border-t-secondary-50" role="status" aria-hidden="true" />
                    Accesso in corso...
                  </>
                ) : (
                  'Accedi'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
